/**
 * A short chime for a new order, made in the browser — no sound file to load.
 *
 * Browsers only let a page play sound after someone has interacted with it,
 * so the audio context is created on the first tap or key press. On a till
 * that is always true by the time an order arrives: the PIN was typed.
 */
const SOUND_KEY = "order_sound";

let context = null;

const prime = () => {
  try {
    if (!context) {
      const AudioContext = window.AudioContext || window.webkitAudioContext;
      if (AudioContext) context = new AudioContext();
    }

    if (context?.state === "suspended") context.resume();
  } catch {
    // No audio on this device; the alert is still shown
  }
};

if (typeof window !== "undefined") {
  ["pointerdown", "keydown", "touchstart"].forEach((type) =>
    window.addEventListener(type, prime, { passive: true }),
  );
}

export const soundEnabled = () => {
  try {
    return localStorage.getItem(SOUND_KEY) !== "off";
  } catch {
    return true;
  }
};

export const setSoundEnabled = (on) => {
  try {
    localStorage.setItem(SOUND_KEY, on ? "on" : "off");
  } catch {
    // Remembered for this session only
  }
};

export const playChime = () => {
  navigator.vibrate?.([120, 60, 120]);

  if (!soundEnabled()) return;

  prime();
  if (!context || context.state !== "running") return;

  const start = context.currentTime;

  // Three rising notes: distinct from a phone ringtone, hard to miss
  [
    [880, 0],
    [1320, 0.16],
    [1760, 0.32],
  ].forEach(([frequency, offset]) => {
    const oscillator = context.createOscillator();
    const gain = context.createGain();

    oscillator.type = "sine";
    oscillator.frequency.value = frequency;

    gain.gain.setValueAtTime(0.0001, start + offset);
    gain.gain.exponentialRampToValueAtTime(0.4, start + offset + 0.02);
    gain.gain.exponentialRampToValueAtTime(0.0001, start + offset + 0.35);

    oscillator.connect(gain).connect(context.destination);
    oscillator.start(start + offset);
    oscillator.stop(start + offset + 0.4);
  });
};
